import {
  decodePeriodValue,
  encodePeriodValue,
  formatPeriodTriggerLabel,
  fromLumenPeriodValue,
  getMonthNames,
  toLumenPeriodValue,
} from '@components'

import { type PeriodValue as LumenPeriodValue, PeriodPicker, type PeriodPickerMode } from '@shinetools/lumen-react'
import { parseISO, startOfDay } from 'date-fns'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { PeriodMode } from '../../../../../../../../enums/periodMode'
import { useLocale } from '../../../../../../../../hooks/useLocale'
import { useOrganizationFiscalYears } from '../../../../../../../../hooks/useOrganizationFiscalYears'
import { usePeriodDates } from '../../../../../../../../hooks/usePeriodDates'
import type { PeriodValue } from '../../../../../../../../types/periodValue'
import { getPeriodValueFromFiscalYear } from '../../../../../../../../utils/getPeriodValueFromFiscalYear'
import type { DateRange } from './types/dateRange'
import { isFinitePeriod } from './utils/isFinitePeriod'
import { isMatchingDateRange } from './utils/isMatchingDateRange'

const AVAILABLE_MODES: readonly PeriodPickerMode[] = ['month', 'quarter', 'fiscalYear', 'custom']

interface SalesReportPeriodPickerProps {
  encodedPeriod: string
  onChange: (value: PeriodValue) => void
}

export const SalesReportPeriodPicker = ({ encodedPeriod, onChange }: SalesReportPeriodPickerProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useLocale()
  const { organization } = useUserOrganization()
  const { defaultYear: currentFiscalYear, maxDate, minDate } = useOrganizationFiscalYears()

  const currentFiscalYearPeriod =
    organization?.id && currentFiscalYear !== undefined
      ? encodePeriodValue(getPeriodValueFromFiscalYear(currentFiscalYear), organization.id)
      : undefined
  const { fromDate: currentFiscalYearStart } = usePeriodDates(currentFiscalYearPeriod)

  const fiscalYearToDateRange: DateRange | undefined = useMemo(
    () =>
      currentFiscalYearStart
        ? { endDate: startOfDay(new Date()), startDate: parseISO(currentFiscalYearStart) }
        : undefined,
    [currentFiscalYearStart],
  )

  const fiscalYearToDateLabel = t('sales_report.period_select.fiscal_year_to_date')
  const value = useMemo(() => toLumenPeriodValue(decodePeriodValue(encodedPeriod)), [encodedPeriod])
  const monthLabels = useMemo(() => getMonthNames(locale.code), [locale.code])

  // Lumen always renders an "All time" shortcut in the Custom panel and gives no way to hide it (FEF-852), so it is
  // relabelled and mapped to the current fiscal year up to today instead of selecting an open-ended period.
  const handleChange = useCallback(
    (lumenValue: LumenPeriodValue | null): void => {
      const periodValue = fromLumenPeriodValue(lumenValue)

      if (periodValue?.mode === PeriodMode.All) {
        if (fiscalYearToDateRange) {
          onChange({ customDate: fiscalYearToDateRange, mode: PeriodMode.CustomDate })
        }

        return
      }

      if (!isFinitePeriod(periodValue)) {
        return
      }

      onChange(periodValue)
    },
    [fiscalYearToDateRange, onChange],
  )

  const handleFormatTriggerLabel = useCallback(
    (lumenValue: LumenPeriodValue) =>
      isMatchingDateRange(lumenValue, fiscalYearToDateRange)
        ? fiscalYearToDateLabel
        : formatPeriodTriggerLabel({ locale, monthLabels, t, value: lumenValue }),
    [fiscalYearToDateLabel, fiscalYearToDateRange, locale, monthLabels, t],
  )

  return (
    <PeriodPicker
      allTimeLabel={fiscalYearToDateLabel}
      availableModes={AVAILABLE_MODES}
      formatTriggerLabel={handleFormatTriggerLabel}
      labels={{
        customMode: t('sales_report.period_select.mode.custom'),
        customRangeTitle: t('period_select.mode.custom_date'),
        endDatePlaceholder: t('period_select.custom_date.date_placeholder'),
        fiscalYearMode: t('sales_report.period_select.mode.fiscal_year'),
        monthMode: t('period_select.mode.month'),
        quarterMode: t('period_select.mode.quarter'),
        startDatePlaceholder: t('period_select.custom_date.date_placeholder'),
      }}
      maxDate={maxDate}
      minDate={minDate}
      monthLabels={monthLabels}
      onChange={handleChange}
      placeholder={t('period_select.trigger_button_placeholder')}
      saveLabel={t('period_select.save_button')}
      shape="rounded"
      value={value}
    />
  )
}
